import { useEffect, useState, useCallback } from "react";
import useLead from "../../../hooks/useLead";
import useHelp from "../../../hooks/useHelp";
import useErroPadrao from "../../../hooks/useErroPadrao";
import { CHAVE_TOKEN, real, dataBR, hojeISO } from "../../../utils/formata";

// INVESTIMENTO & FUNIL (Dani, 19/09: "sessão de investimento, meta ads … funil de quantas pessoas chamou, quantas fechou
// e quantas foram reprovadas … com gráfico"). O Meta diz quanto foi investido e quantas conversas o anúncio abriu
// (função /meta/help no servidor do Pages); o robô diz o que aconteceu com cada pessoa — ele grava no cadastro do contato
// a linha "FUNIL: <etapa> · origem <anuncio|direto|prospeccao> · chegou <etapa máxima> · valor … · cidade … · motivo …".
const ETAPAS = [
    ["chamou", "Chamaram no WhatsApp"],
    ["pediu_credito", "Pediram crédito"],
    ["documento", "Mandaram documento"],
    ["analise", "Aceitaram a proposta"],
    ["fechou", "Contratos"],
];
const PERIODOS = [["7", "7 dias"], ["30", "30 dias"], ["mes", "Este mês"], ["tudo", "Tudo"]];
const CHAVE_PERIODO = "funil-periodo";
const CHAVE_ORIGEM = "funil-origem";
const cor = { ouro: "#d4af37", verde: "#2ecc71", vermelho: "#ff6b5b", azul: "#4da3ff", roxo: "#a78bfa", texto: "#e8eef5", suave: "#9aa5b5", fundo: "#0f1e2e", linha: "#1b2b40" };
const cartao = { background: cor.fundo, borderRadius: 12, padding: "12px 14px", minWidth: 0 };
const rotulo = { color: cor.suave, fontSize: "0.72rem", letterSpacing: 0.6, textTransform: "uppercase" };
const numero = { fontSize: "1.45rem", fontWeight: 800, lineHeight: 1.15, color: cor.texto };

// "FUNIL: fechou · origem anuncio · chegou fechou · valor 1500 · desde 2026-09-17 · ad 1202… · cidade Penha · motivo …"
function lerFunil(obs) {
    const linha = (String(obs || "").match(/^FUNIL:.*$/m) || [""])[0];
    if (!linha) return null;
    const partes = linha.replace(/^FUNIL:\s*/, "").split(" · ");
    const r = { etapa: partes[0] };
    for (const p of partes.slice(1)) { const i = p.indexOf(" "); if (i > 0) r[p.slice(0, i)] = p.slice(i + 1).replace(/^"|"$/g, ""); }
    return r;
}
// campanha de quem veio do anúncio: pelo número do anúncio (exato); sem ele, a única campanha da Help que abriu conversa
// naquele dia (provável); se duas rodavam no dia, fica "sem rastro" — não chuta. (A mesma regra do resumo diário do robô.)
function campanhaDoContato(f, meta) {
    if (!f || f.origem !== "anuncio") return null;
    if (f.ad && meta?.anuncios) { const ad = meta.anuncios.find((a) => String(a.id) === String(f.ad)); if (ad) return { nome: ad.campanha, provavel: false }; }
    if (f.desde && meta?.porDiaCampanha) {
        const doDia = [...new Set(meta.porDiaCampanha.filter((x) => x.data === f.desde && x.conversas > 0).map((x) => x.campanha))];
        if (doDia.length === 1) return { nome: doDia[0], provavel: true };
    }
    return { nome: "", provavel: true };
}
const nomeCurto = (nome) => { const m = String(nome || "").match(/conversas-([^-]+)/i); return m ? m[1] : String(nome || ""); };
const diaLocal = (iso) => new Date(iso).toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
const somaDias = (dia, n) => { const d = new Date(`${dia}T12:00:00`); d.setDate(d.getDate() + n); return d.toLocaleDateString("sv-SE"); };
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const ler = (k, padrao) => { try { return localStorage.getItem(k) || padrao; } catch { return padrao; } };
const gravar = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

function intervalo(periodo) {
    const hoje = hojeISO();
    if (periodo === "7") return [somaDias(hoje, -6), hoje];
    if (periodo === "30") return [somaDias(hoje, -29), hoje];
    if (periodo === "mes") return [`${hoje.slice(0, 8)}01`, hoje];
    return ["2026-08-01", hoje];
}

// gráfico do dia a dia: barras = pessoas que chamaram (robô), linha = investimento (Meta)
function GraficoDiario({ dias }) {
    // tela estreita desenha menor pra letra do eixo não virar formiga no celular
    const estreito = typeof window !== "undefined" && window.innerWidth < 600;
    const L = estreito ? 420 : 720, A = estreito ? 250 : 230, m = { e: 30, d: 44, t: 14, b: 30 };
    const w = L - m.e - m.d, h = A - m.t - m.b;
    const maxP = Math.max(1, ...dias.map((d) => d.pessoas));
    const maxR = Math.max(1, ...dias.map((d) => d.investido));
    const passo = w / Math.max(1, dias.length);
    const larg = Math.max(3, Math.min(26, passo * 0.62));
    const x = (i) => m.e + passo * i + passo / 2;
    const yP = (v) => m.t + h - (v / maxP) * h;
    const yR = (v) => m.t + h - (v / maxR) * h;
    const temInvest = dias.some((d) => d.investido > 0);
    const pontos = dias.map((d, i) => `${x(i)},${yR(d.investido)}`).join(" ");
    const cadaRotulo = Math.ceil(dias.length / (estreito ? 5 : 8));
    return (
        <svg viewBox={`0 0 ${L} ${A}`} style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label="Pessoas que chamaram e investimento por dia">
            {[0, 0.5, 1].map((f) => (
                <g key={f}>
                    <line x1={m.e} x2={L - m.d} y1={m.t + h * (1 - f)} y2={m.t + h * (1 - f)} stroke={cor.linha} />
                    <text x={m.e - 6} y={m.t + h * (1 - f) + 4} fill={cor.suave} fontSize="11" textAnchor="end">{Math.round(maxP * f)}</text>
                    {temInvest && <text x={L - m.d + 6} y={m.t + h * (1 - f) + 4} fill={cor.ouro} fontSize="11">{`R$${Math.round(maxR * f)}`}</text>}
                </g>
            ))}
            {dias.map((d, i) => (
                <g key={d.data}>
                    <rect x={x(i) - larg / 2} y={yP(d.pessoas)} width={larg} height={Math.max(0, m.t + h - yP(d.pessoas))} rx="3" fill={cor.azul} opacity="0.85">
                        <title>{`${dataBR(d.data)}: ${d.pessoas} pessoa(s)${temInvest ? ` · ${real(d.investido)} investidos` : ""}`}</title>
                    </rect>
                    {i % cadaRotulo === 0 && <text x={x(i)} y={A - 10} fill={cor.suave} fontSize="11" textAnchor="middle">{dataBR(d.data).slice(0, 5)}</text>}
                </g>
            ))}
            {temInvest && <polyline points={pontos} fill="none" stroke={cor.ouro} strokeWidth="2.5" strokeLinejoin="round" />}
            {temInvest && dias.map((d, i) => <circle key={d.data} cx={x(i)} cy={yR(d.investido)} r="3" fill={cor.ouro} />)}
        </svg>
    );
}

function Barras({ itens, corBarra }) {
    const max = Math.max(1, ...itens.map((i) => i.valor));
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {itens.map((i) => (
                <div key={i.nome}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: "0.85rem", marginBottom: 3 }}>
                        <span style={{ color: cor.texto, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.nome}</span>
                        <strong style={{ color: corBarra, flexShrink: 0 }}>{i.rotulo ?? i.valor}</strong>
                    </div>
                    <div style={{ height: 7, background: cor.linha, borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(i.valor / max) * 100}%`, background: corBarra, borderRadius: 4 }}></div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function InvestimentoFunil() {
    const [periodo, setPeriodo] = useState(() => ler(CHAVE_PERIODO, "30"));
    const [origem, setOrigem] = useState(() => ler(CHAVE_ORIGEM, "todos"));
    const [leads, setLeads] = useState(null);
    const [emprestimos, setEmprestimos] = useState([]);
    const [meta, setMeta] = useState(null); // null = carregando; { configurado:false } | { configurado:true, ... } | { erro }
    const { listarLeads } = useLead();
    const { consultarEmprestimos } = useHelp();
    const { setErroPadrao } = useErroPadrao();
    const [de, ate] = intervalo(periodo);

    const carregar = useCallback(() => {
        Promise.all([listarLeads(), consultarEmprestimos()])
            .then(([l, e]) => { setLeads(l || []); setEmprestimos(e || []); })
            .catch((error) => { setLeads([]); setErroPadrao(error); });
    }, [listarLeads, consultarEmprestimos, setErroPadrao]);
    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        setMeta(null);
        const token = ler(CHAVE_TOKEN, "");
        fetch(`/meta/help?de=${de}&ate=${ate}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(async (r) => {
                const j = await r.json().catch(() => ({}));
                if (r.ok) return setMeta(j);
                setMeta(r.status === 404 ? { configurado: false } : { configurado: true, erro: j.message || `erro ${r.status}` });
            })
            .catch(() => setMeta({ configurado: false }));
    }, [de, ate]);

    const escolherPeriodo = (v) => { setPeriodo(v); gravar(CHAVE_PERIODO, v); };
    const escolherOrigem = (v) => { setOrigem(v); gravar(CHAVE_ORIGEM, v); };

    if (!leads) return <div style={{ textAlign: "center", padding: "3em" }}><span className="spinner-border" style={{ color: cor.ouro }}></span></div>;

    // contatos que conversaram com o robô: a linha FUNIL que ele grava no cadastro (etapa, origem, data do 1º contato, anúncio…)
    const oito = (t) => String(t || "").replace(/\D/g, "").slice(-8);
    const temMeta = !!(meta?.configurado && !meta.erro && meta.total);
    const todos = leads.map((l) => {
        const f = lerFunil(l.observacao);
        if (!f) return null;
        return { ...f, id: l.id, dia: f.desde || diaLocal(l.createdAt), valor: Number(f.valor) || 0, tel: oito(l.telefone) };
    }).filter(Boolean);
    const fichaDoTel = new Map(todos.map((p) => [p.tel, p]));
    const campanhaDe = (p) => campanhaDoContato(p, temMeta ? meta : null);
    const noPeriodoTodos = todos.filter((p) => p.dia >= de && p.dia <= ate);
    const noPeriodo = noPeriodoTodos.filter((p) => origem === "todos" || p.origem === "anuncio");
    const pessoasAnuncio = noPeriodoTodos.filter((p) => p.origem === "anuncio");

    // CONTRATOS (Dani, 19/09: "só contar pelos contratos"): os do sistema, pela data do contrato; cancelado fica fora
    const dataDoContrato = (e) => String(e.dataContrato || "").slice(0, 10);
    const contratosPeriodo = emprestimos.filter((e) => e.status !== "cancelado" && dataDoContrato(e) >= de && dataDoContrato(e) <= ate);
    const fichaDoContrato = (e) => fichaDoTel.get(oito(e.Empresa?.telefone));
    const contratosAnuncio = contratosPeriodo.filter((e) => fichaDoContrato(e)?.origem === "anuncio");
    const contratos = origem === "todos" ? contratosPeriodo : contratosAnuncio;

    const idx = (e) => ETAPAS.findIndex(([k]) => k === e);
    const chegaram = [
        ...ETAPAS.slice(0, 4).map(([k, nome], i) => ({ k, nome, n: noPeriodo.filter((p) => idx(p.chegou) >= i).length })),
        { k: "contrato", nome: "Contratos", n: contratos.length },
    ];
    const reprovados = noPeriodo.filter((p) => p.etapa === "reprovado");
    const desistiram = noPeriodo.filter((p) => p.etapa === "desistiu");
    const emAndamento = noPeriodo.filter((p) => !["fechou", "reprovado", "desistiu"].includes(p.etapa)).length;

    // dinheiro: o que está nos contratos (valor emprestado e o total a receber), não o que a pessoa pediu
    const lucroDe = (lista) => lista.reduce((s, e) => s + Number(e.valorTotal || 0) - Number(e.valorPrincipal || 0), 0);
    const emprestado = contratos.reduce((s, e) => s + Number(e.valorPrincipal || 0), 0);
    const lucroPrevisto = lucroDe(contratos);
    const lucroAnuncio = lucroDe(contratosAnuncio);
    const investido = temMeta ? meta.total.investido : null;
    const custoPessoa = temMeta && pessoasAnuncio.length ? investido / pessoasAnuncio.length : null;
    const custoContrato = temMeta && contratosAnuncio.length ? investido / contratosAnuncio.length : null;
    const roi = temMeta && investido > 0 ? Math.round(((lucroAnuncio - investido) / investido) * 100) : null;

    // por campanha: número do anúncio (exato) ou, sem ele, a única campanha que abriu conversa naquele dia (provável)
    const linhasCampanha = temMeta ? meta.porCampanha.map((c) => {
        const pessoas = pessoasAnuncio.filter((p) => campanhaDe(p)?.nome === c.nome).length;
        const ctt = contratosAnuncio.filter((e) => campanhaDe(fichaDoContrato(e))?.nome === c.nome);
        return { ...c, pessoas, contratos: ctt.length, emprestado: ctt.reduce((s, e) => s + Number(e.valorPrincipal || 0), 0), custoContrato: ctt.length ? c.investido / ctt.length : null };
    }) : [];
    const semRastro = temMeta ? {
        pessoas: pessoasAnuncio.filter((p) => !campanhaDe(p)?.nome).length,
        contratos: contratosAnuncio.filter((e) => !campanhaDe(fichaDoContrato(e))?.nome).length,
    } : null;

    const dias = [];
    for (let d = de; d <= ate && dias.length < 400; d = somaDias(d, 1)) {
        dias.push({ data: d, pessoas: noPeriodo.filter((p) => p.dia === d).length, investido: temMeta ? Number(meta.porDia.find((x) => x.data === d)?.investido || 0) : 0 });
    }
    const primeiroComDado = dias.findIndex((d) => d.pessoas > 0 || d.investido > 0);
    // dias vazios do começo (antes do primeiro anúncio/contato) só espremem o gráfico
    const diasGrafico = periodo !== "7" && primeiroComDado > 0 ? dias.slice(primeiroComDado) : dias;
    const comMovimento = diasGrafico.filter((d) => d.pessoas > 0 || d.investido > 0);
    const melhorDia = [...diasGrafico].sort((a, b) => b.pessoas - a.pessoas || b.investido - a.investido)[0];
    const mediaDia = comMovimento.length ? noPeriodo.length / comMovimento.length : 0;

    // onde o funil perde gente pra reprovação: a última etapa que a pessoa alcançou antes de ser reprovada
    const reprovadosPorEtapa = ETAPAS.slice(0, 4).map(([k, nome]) => ({ nome: nome.replace("Chamaram no WhatsApp", "Logo na conversa"), valor: reprovados.filter((p) => p.chegou === k).length })).filter((i) => i.valor > 0);
    const semMotivo = reprovados.filter((p) => !p.motivo || /^(sua an[aá]lise foi|o cr[eé]dito foi) (recusad|reprovad)/i.test(p.motivo)).length;
    const cidades = Object.entries(noPeriodo.reduce((o, p) => { const k = (p.cidade || "não informou").replace(/\s+(sc|pr|rs)$/i, "").replace(/^\w/, (c) => c.toUpperCase()); o[k] = o[k] || { n: 0, f: 0 }; o[k].n++; if (p.etapa === "fechou") o[k].f++; return o; }, {}))
        .sort((a, b) => (a[0] === "Não informou") - (b[0] === "Não informou") || b[1].n - a[1].n).slice(0, 6).map(([nome, v]) => ({ nome, valor: v.n, rotulo: `${v.n}${v.f ? ` · ${v.f} com contrato` : ""}` }));

    const kpis = [
        { r: "Investido no Meta", v: temMeta ? real(investido) : "—", s: temMeta ? `${meta.total.conversas} conversas abertas pelo anúncio` : "conecte o Meta abaixo", c: cor.ouro },
        { r: "Pessoas que chamaram", v: noPeriodo.length, s: custoPessoa != null ? `${pessoasAnuncio.length} do anúncio · ${real(custoPessoa)} cada` : `${pessoasAnuncio.length} vieram do anúncio`, c: cor.azul },
        { r: "Contratos", v: contratos.length, s: custoContrato != null ? `${contratosAnuncio.length} do anúncio · ${real(custoContrato)} cada` : `${contratosAnuncio.length} vieram do anúncio`, c: cor.verde },
        { r: "Reprovadas", v: reprovados.length, s: `${pct(reprovados.length, noPeriodo.length)}% de quem chamou`, c: cor.vermelho },
        { r: "Emprestado", v: real(emprestado), s: `lucro previsto ${real(lucroPrevisto)}`, c: cor.texto },
        { r: "Retorno do anúncio", v: roi != null ? `${roi > 0 ? "+" : ""}${roi}%` : "—", s: roi != null ? `lucro ${real(lucroAnuncio)} dos contratos do anúncio` : "precisa do Meta conectado", c: roi != null && roi >= 0 ? cor.verde : cor.vermelho },
    ];

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                <div className="filtros-status" style={{ margin: 0 }}>
                    {PERIODOS.map(([k, t]) => <button key={k} type="button" className={`btn btn-sm ${periodo === k ? "btn-he" : "btn-outline-he"}`} onClick={() => escolherPeriodo(k)}>{t}</button>)}
                </div>
                <div className="filtros-status" style={{ margin: 0 }}>
                    {[["anuncio", "Só do anúncio"], ["todos", "Todas as origens"]].map(([k, t]) => <button key={k} type="button" className={`btn btn-sm ${origem === k ? "btn-he" : "btn-outline-he"}`} onClick={() => escolherOrigem(k)}>{t}</button>)}
                </div>
            </div>
            <div style={{ color: cor.suave, fontSize: "0.8rem", marginBottom: 12 }}>{dataBR(de)} a {dataBR(ate)}{temMeta ? ` · Meta atualizado ${new Date(meta.atualizadoEm).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}</div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))", gap: 10, marginBottom: 14 }}>
                {kpis.map((k) => (
                    <div key={k.r} style={{ ...cartao, borderTop: `3px solid ${k.c}` }}>
                        <div style={rotulo}>{k.r}</div>
                        <div style={{ ...numero, color: k.c === cor.texto ? cor.texto : k.c }}>{k.v}</div>
                        <div style={{ color: cor.suave, fontSize: "0.78rem", marginTop: 2 }}>{k.s}</div>
                    </div>
                ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, marginBottom: 14 }}>
                <div className="he-card" style={{ margin: 0 }}>
                    <h5 className="he-titulo he-dourado"><i className="bi bi-funnel"></i> Funil</h5>
                    {temMeta &&
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", color: cor.suave, marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
                            <span>Viram o anúncio: <strong style={{ color: cor.texto }}>{meta.total.alcance.toLocaleString("pt-BR")}</strong></span>
                            <span>Clicaram: <strong style={{ color: cor.texto }}>{meta.total.cliques.toLocaleString("pt-BR")}</strong></span>
                            <span>Abriram conversa: <strong style={{ color: cor.texto }}>{meta.total.conversas}</strong></span>
                        </div>}
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {chegaram.map((e, i) => {
                            const larg = Math.min(100, Math.max(8, pct(e.n, chegaram[0].n || 1)));
                            const doAnterior = i ? pct(e.n, chegaram[i - 1].n) : 100;
                            return (
                                <div key={e.k}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", marginBottom: 3, gap: 8 }}>
                                        <span style={{ color: cor.texto }}>{e.nome}</span>
                                        <span style={{ color: cor.suave, whiteSpace: "nowrap" }}><strong style={{ color: cor.texto }}>{e.n}</strong>{i > 0 && doAnterior <= 100 ? ` · ${doAnterior}% da etapa anterior` : ""}</span>
                                    </div>
                                    <div style={{ height: 22, background: cor.linha, borderRadius: 6, overflow: "hidden" }}>
                                        <div style={{ height: "100%", width: `${larg}%`, margin: "0 auto", borderRadius: 6, background: e.k === "contrato" ? cor.verde : `linear-gradient(90deg, ${cor.ouro}, ${cor.roxo})` }}></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 12 }}>
                        {[["Reprovadas", reprovados.length, cor.vermelho], ["Desistiram", desistiram.length, cor.roxo], ["Sem desfecho", emAndamento, cor.azul]].map(([t, n, c]) => (
                            <div key={t} style={{ ...cartao, padding: "8px 10px", textAlign: "center" }}>
                                <div style={{ color: c, fontWeight: 800, fontSize: "1.15rem" }}>{n}</div>
                                <div style={{ color: cor.suave, fontSize: "0.75rem" }}>{t}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="he-card" style={{ margin: 0 }}>
                    <h5 className="he-titulo he-dourado"><i className="bi bi-bar-chart-line"></i> Dia a dia</h5>
                    <div style={{ display: "flex", gap: 14, fontSize: "0.8rem", color: cor.suave, marginBottom: 6, flexWrap: "wrap" }}>
                        <span><span style={{ display: "inline-block", width: 10, height: 10, background: cor.azul, borderRadius: 2, marginRight: 5 }}></span>Pessoas que chamaram</span>
                        {temMeta && <span><span style={{ display: "inline-block", width: 14, height: 3, background: cor.ouro, marginRight: 5, verticalAlign: "middle" }}></span>Investimento no Meta</span>}
                    </div>
                    <GraficoDiario dias={diasGrafico} />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 12 }}>
                        {[
                            ["Melhor dia", melhorDia && melhorDia.pessoas ? `${dataBR(melhorDia.data).slice(0, 5)} · ${melhorDia.pessoas}` : "—"],
                            ["Média por dia", mediaDia ? `${String(Math.round(mediaDia * 10) / 10).replace(".", ",")} pessoas` : "—"],
                            ["Custo por conversa", temMeta && meta.total.conversas ? real(investido / meta.total.conversas) : "—"],
                        ].map(([t, v]) => (
                            <div key={t} style={{ ...cartao, padding: "8px 10px", textAlign: "center" }}>
                                <div style={{ color: cor.texto, fontWeight: 800, fontSize: "1rem", whiteSpace: "nowrap" }}>{v}</div>
                                <div style={{ color: cor.suave, fontSize: "0.75rem" }}>{t}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginBottom: 14 }}>
                <div className="he-card" style={{ margin: 0 }}>
                    <h5 className="he-titulo he-dourado"><i className="bi bi-geo-alt"></i> De onde vêm</h5>
                    {cidades.length ? <Barras itens={cidades} corBarra={cor.azul} /> : <p style={{ color: cor.suave, margin: 0 }}>Ninguém no período.</p>}
                </div>
                <div className="he-card" style={{ margin: 0 }}>
                    <h5 className="he-titulo" style={{ color: cor.vermelho }}><i className="bi bi-x-octagon"></i> Em que etapa reprovaram</h5>
                    {reprovadosPorEtapa.length ? <Barras itens={reprovadosPorEtapa} corBarra={cor.vermelho} /> : <p style={{ color: cor.suave, margin: 0 }}>Nenhuma reprovação no período.</p>}
                    {semMotivo > 0 &&
                        <p style={{ color: cor.suave, fontSize: "0.8rem", margin: "10px 0 0" }}>
                            {semMotivo} de {reprovados.length} sem o motivo escrito. Escrevendo o porquê na recusa (ex.: "sem ponto físico", "fora da região"), dá pra ver aqui o que filtrar no anúncio.
                        </p>}
                </div>
            </div>

            {temMeta && linhasCampanha.length > 0 &&
                <div className="he-card sem-rolagem" style={{ marginBottom: 14 }}>
                    <h5 className="he-titulo he-dourado"><i className="bi bi-megaphone"></i> De qual campanha vem</h5>
                    <div className="tabela-rolavel rolagem-so-desktop">
                        <table className="table table-striped tabela-responsiva tabela-mini" style={{ fontSize: "0.88rem" }}>
                            <thead><tr><th>Campanha</th><th>Investido</th><th>Conversas</th><th>Pessoas no robô</th><th>Contratos</th><th>Custo por contrato</th><th>Custo por conversa</th></tr></thead>
                            <tbody>
                                {linhasCampanha.map((c) => (
                                    <tr key={c.id}>
                                        <td className="principal" data-rotulo="Campanha" style={{ wordBreak: "break-word" }} title={c.nome}>{nomeCurto(c.nome)}</td>
                                        <td data-rotulo="Investido">{real(c.investido)}</td>
                                        <td data-rotulo="Conversas">{c.conversas}</td>
                                        <td data-rotulo="Pessoas no robô">{c.pessoas}</td>
                                        <td className="destaque" data-rotulo="Contratos">{c.contratos}{c.emprestado ? ` · ${real(c.emprestado)}` : ""}</td>
                                        <td data-rotulo="Custo por contrato">{c.custoContrato != null ? real(c.custoContrato) : "—"}</td>
                                        <td data-rotulo="Custo por conversa">{c.custoConversa != null ? real(c.custoConversa) : "—"}</td>
                                    </tr>
                                ))}
                                {semRastro && (semRastro.pessoas > 0 || semRastro.contratos > 0) &&
                                    <tr>
                                        <td className="principal" data-rotulo="Campanha" style={{ color: cor.suave }}>Anúncio, campanha sem rastro</td>
                                        <td data-rotulo="Investido">—</td>
                                        <td data-rotulo="Conversas">—</td>
                                        <td data-rotulo="Pessoas no robô">{semRastro.pessoas}</td>
                                        <td className="destaque" data-rotulo="Contratos">{semRastro.contratos}</td>
                                        <td data-rotulo="Custo por contrato">—</td>
                                        <td data-rotulo="Custo por conversa">—</td>
                                    </tr>}
                            </tbody>
                        </table>
                    </div>
                    <p style={{ color: cor.suave, fontSize: "0.78rem", margin: "8px 0 0" }}>
                        Desde 19/09 cada pessoa que clica no anúncio chega com o número dele, e a campanha é exata. Antes disso, quem chegou num dia em que só uma campanha trazia conversa conta pra ela; quem chegou com duas rodando fica "sem rastro".
                    </p>
                </div>
            }

            {meta && !temMeta &&
                <div className="he-card" style={{ borderColor: "rgba(212,175,55,0.5)", marginBottom: 14 }}>
                    <h5 className="he-titulo he-dourado"><i className="bi bi-plug"></i> {meta.erro ? "O Meta não respondeu agora" : "Conectar o Meta Ads"}</h5>
                    {meta.erro ?
                        <p style={{ color: cor.suave, margin: 0 }}>{meta.erro}. O funil acima continua certo (vem do robô); o investimento volta sozinho quando o Meta responder.</p> :
                        <ol style={{ color: cor.texto, margin: 0, paddingLeft: "1.2em", lineHeight: 1.7 }}>
                            <li>Abra <strong>dash.cloudflare.com</strong> → <strong>Workers &amp; Pages</strong> → <strong>help-empresarial</strong> → <strong>Settings</strong> → <strong>Variables and Secrets</strong>.</li>
                            <li><strong>Add</strong> → tipo <strong>Secret</strong> → nome <strong>META_TOKEN</strong> → cole a chave da Fada Gestora (arquivo <em>fada gestora.txt</em>, pasta Tokens) → <strong>Save</strong>.</li>
                            <li>Pronto: na próxima publicação do painel o investimento, o custo por pessoa e o retorno aparecem aqui sozinhos.</li>
                        </ol>}
                </div>
            }
        </div>
    );
}
